import React, { useState, useEffect, useMemo } from 'react';
import { Container, Table, Form, Row, Col, Alert, Spinner, Card, Modal, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faFileAlt, faPlus } from '@fortawesome/free-solid-svg-icons';
import api, { getOrders, getOrderDetails } from '../services/api';
import { toast } from 'react-hot-toast';
import moment from 'moment';
import { API_URL } from '../services/api';
import DocumentViewerModal from './DocumentViewerModal';

function Orders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orderDetails, setOrderDetails] = useState({
    items: [],
    rates: [],
    documents: []
  });
  
  // Document viewer modal state
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  
  const [dateRange, setDateRange] = useState({
    startDate: new Date(new Date().setDate(new Date().getDate() - 30)).toISOString().split('T')[0], // Last 30 days
    endDate: new Date().toISOString().split('T')[0] // Today
  });

  const fetchOrders = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getOrders(dateRange.startDate, dateRange.endDate);
      setOrders(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching orders:', error);
      setError('Failed to fetch orders. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, [dateRange]);

  const handleDateChange = (e) => {
    const { name, value } = e.target;
    setDateRange(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleApplyDateFilter = () => {
    if (dateRange.startDate <= dateRange.endDate) {
      fetchOrders();
    } else {
      toast.error("Start date must be before end date");
    }
  };

  const handleOrderClick = async (order) => {
    try {
      setSelectedOrder(order);
      
      // Load order details
      const details = await getOrderDetails(order.idTrans);
      setOrderDetails(details);
      setShowDetailModal(true);
    } catch (error) {
      console.error("Failed to load order details:", error);
      toast.error(`Failed to load order details: ${error.message || 'Unknown error'}`);
    }
  };

  // Format date to MM/DD/YY
  const formatDate = (dateString) => {
    if (!dateString) return '';
    return moment(dateString).format('MM/DD/YY');
  };

  // Format currency for display
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount || 0);
  };

  // Calculate grand total for rates
  const grandTotal = useMemo(() => {
    if (!orderDetails?.rates || orderDetails.rates.length === 0) return 0;
    
    return orderDetails.rates.reduce((sum, rate) => {
      const qty = parseFloat(rate.qty) || 0;
      const rateValue = parseFloat(rate.rate) || 0;
      const total = qty * rateValue;
      return sum + total;
    }, 0);
  }, [orderDetails?.rates]);

  // Generate receipt for the order
  const handlePrintReceipt = async () => {
    if (!selectedOrder) return;
    
    try {
      // Show loading state
      toast.loading("Generating receipt...");
      
      // Call API to generate receipt PDF
      const response = await api.post(`/api/orders/${selectedOrder.idTrans}/receipt`, {
        includeDetails: true
      }, {
        responseType: 'blob'
      });
      
      // Get content type from response
      const contentType = response.headers['content-type'];
      console.log("Receipt PDF content type:", contentType);
      
      // Create a blob URL for the PDF
      const blob = new Blob([response.data], { type: contentType });
      const blobUrl = URL.createObjectURL(blob);
      
      // Prepare document data for the viewer
      const receiptDocument = {
        descr: `Receipt for Order ${selectedOrder.idTrans}`,
        fileName: `Receipt_${selectedOrder.idTrans}.pdf`,
        blobUrl: blobUrl, // Use blob URL instead of base64
        type: contentType || 'application/pdf'
      };
      
      // Close loading toast
      toast.dismiss();
      
      // Show success message
      toast.success("Receipt generated successfully");
      
      // Show the document in the viewer modal
      setSelectedDocument(receiptDocument);
      setShowDocumentModal(true);
    } catch (error) {
      console.error("Failed to generate receipt:", error);
      toast.dismiss();
      toast.error(`Failed to generate receipt: ${error.message || 'Unknown error'}`);
    }
  };

  // Handle document click to open in modal
  const handleDocumentClick = async (doc, e) => {
    e.preventDefault();
    
    // Check if this is the receipt document
    if (doc.isReceipt) {
      handlePrintReceipt();
      return;
    }
    
    // Show loading toast
    toast.loading("Loading document...");
    
    try {
      // If we have a blobUrl, use it directly
      if (doc.blobUrl) {
        // Prepare document data for the viewer
        const documentData = {
          ...doc,
          url: doc.blobUrl
        };
        
        // Dismiss loading toast
        toast.dismiss();
        
        // Show the document in the modal
        setSelectedDocument(documentData);
        setShowDocumentModal(true);
      } else if (doc.fileName) {
        // Try to get the document URL from the server first
        try {
          const urlResponse = await api.get(`/api/orders/documents/${doc.fileName}`);
          
          const urlData = urlResponse.data;
          
          if (urlData && urlData.url) {
            // Server returned a signed URL (Azure)
            const documentData = {
              ...doc,
              url: urlData.url
            };
            
            // Dismiss loading toast
            toast.dismiss();
            
            // Show the document in the modal
            setSelectedDocument(documentData);
            setShowDocumentModal(true);
          } else {
            throw new Error('No URL returned from server');
          }
        } catch (urlError) {
          // If URL fetch fails, try to fetch the document directly as blob
          try {
            const response = await api.get(`/api/orders/documents/${doc.fileName}`, {
              responseType: 'blob'
            });
            
            // Convert blob to base64 to display in the document viewer
            const responseBlob = response.data;
            const reader = new FileReader();
            reader.readAsDataURL(responseBlob);
            reader.onloadend = () => {
              const base64data = reader.result;
              
              // Prepare document data for the viewer with base64 content
              const documentWithData = {
                ...doc,
                fileData: base64data
              };
              
              // Dismiss loading toast
              toast.dismiss();
              
              // Show the document in the modal
              setSelectedDocument(documentWithData);
              setShowDocumentModal(true);
            };
          } catch (fetchError) {
            // If document fetch fails, show an error message
            toast.dismiss();
            toast.error(`Document not found: ${doc.descr || doc.fileName}`);
            console.error("Document fetch failed:", fetchError);
          }
        }
      } else {
        // No valid document source
        toast.dismiss();
        toast.error("No valid document source found");
      }
    } catch (error) {
      console.error("Failed to load document:", error);
      toast.dismiss();
      toast.error(`Failed to load document: ${error.message || 'Unknown error'}`);
    }
  };

  // Add this function for handling document uploads
  const handleDocumentUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    // Show loading toast
    const loadingToast = toast.loading(`Uploading ${files.length} document(s)...`);

    try {
      for (const file of files) {
        // Create form data
        const formData = new FormData();
        formData.append('file', file);
        formData.append('descr', file.name); // Use filename as description

        // Upload the file
        const response = await api.post(`/api/orders/${selectedOrder.idTrans}/documents`, formData);
        
        const responseData = response.data;
        // Update the documents list in orderDetails
        setOrderDetails(prev => ({
          ...prev,
          documents: [...prev.documents, responseData]
        }));
      }

      toast.success(`Successfully uploaded ${files.length} document(s)`);
    } catch (error) {
      console.error('Failed to upload document:', error);
      toast.error(`Failed to upload document: ${error.message || 'Unknown error'}`);
    } finally {
      toast.dismiss(loadingToast);
    }
  };

  // Add this function before the return statement
  const getCardStyle = (contentType, rows) => {
    // If content is empty, make it very compact
    if (!rows || rows.length === 0) {
      return { 
        maxHeight: 'none',
        minHeight: 'auto',
        height: 'auto'
      };
    }
    
    // For small number of rows, make it compact with no extra space
    if (rows.length <= 3) {
      return { 
        maxHeight: 'none',
        minHeight: 'auto',
        height: 'auto'
      };
    }
    
    // For medium number of rows (4-6), allow a bit more height but still compact
    if (rows.length <= 6) {
      return { 
        maxHeight: '180px',
        overflowY: 'auto',
        minHeight: 'auto'
      };
    }
    
    // For many rows, limit height and add scroll
    return { 
      maxHeight: '200px', 
      overflowY: 'auto',
      minHeight: 'auto'
    };
  };

  if (loading) {
    return (
      <div className="text-center p-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  return (
    <Container fluid className="px-2">
      <Card className="shadow-sm">
        <Card.Header className="py-3">
          <div className="d-flex justify-content-between align-items-center flex-wrap">
            <h5 className="m-0 font-weight-bold text-primary">
              Transactions
              <small className="ms-2 text-muted" style={{ fontSize: '0.7rem' }}>Click on a transaction for more info</small>
            </h5>
            <div className="d-flex gap-3 flex-wrap mt-2 mt-sm-0">
              <div className="d-flex align-items-center">
                <Form.Label className="me-2 mb-0">From:</Form.Label>
              <Form.Control
                type="date"
                name="startDate"
                value={dateRange.startDate}
                onChange={handleDateChange}
                size="sm"
              />
              </div>
              <div className="d-flex align-items-center">
                <Form.Label className="me-2 mb-0">To:</Form.Label>
              <Form.Control
                type="date"
                name="endDate"
                value={dateRange.endDate}
                onChange={handleDateChange}
                size="sm"
              />
              </div>
              <Button 
                variant="primary" 
                size="sm"
                onClick={handleApplyDateFilter}
              >
                <FontAwesomeIcon icon={faSearch} className="me-1" />
                Search
              </Button>
            </div>
          </div>
      </Card.Header>
        <Card.Body className="p-0">
        {error && (
            <Alert variant="danger" className="m-3">
            {error}
          </Alert>
        )}
        
        {orders.length === 0 ? (
            <Alert variant="info" className="m-3">
            No orders found for the selected date range.
          </Alert>
        ) : (
            <div style={{ height: 'calc(100vh - 200px)', minHeight: '400px', overflowY: 'auto' }}>
              <Table hover size="sm" className="mb-0">
                <thead style={{ position: 'sticky', top: 0, background: 'white', zIndex: 1 }}>
                <tr>
                  <th>Order #</th>
                  <th>Date</th>
                  <th>Type</th>
                  <th>Reference</th>
                  <th>Ship To</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((order) => (
                    <tr 
                      key={order.idTrans} 
                      onClick={() => handleOrderClick(order)} 
                      style={{ cursor: 'pointer' }}
                    >
                    <td>{order.idTrans}</td>
                      <td>{formatDate(order.transDate)}</td>
                    <td>
                      {order.approvalStatus === 'pending' ? (
                        <span className="badge bg-warning text-dark">Pending Approval</span>
                      ) : order.approvalStatus === 'rejected' ? (
                        <span className="badge bg-danger">Rejected</span>
                      ) : (
                        order.typeName
                      )}
                    </td>
                    <td>{order.CustomerRef || '-'}</td>
                    <td>{order.delName || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </Card.Body>
    </Card>

      {/* Order Detail Modal */}
      <Modal size="xl" show={showDetailModal} onHide={() => setShowDetailModal(false)}>
        <Modal.Header closeButton className="py-2">
          <Modal.Title>Order Details - #{selectedOrder?.idTrans}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-2">
          <Row className="g-2 mb-2 equal-height-row">
            <Col md={6} className="d-flex">
              <Card className="shadow-sm flex-fill">
                <Card.Header className="py-1">
                  <h6 className="mb-0 d-inline-block me-3">Order Information</h6>
                </Card.Header>
                <Card.Body className="py-1 px-3">
                  <p className="mb-0 small"><strong>Order #:</strong> {selectedOrder?.idTrans}</p>
                  <p className="mb-0 small"><strong>Date:</strong> {formatDate(selectedOrder?.transDate)}</p>
                  <p className="mb-0 small"><strong>Type:</strong> {selectedOrder?.typeName}</p>
                  <p className="mb-0 small"><strong>Reference:</strong> {selectedOrder?.CustomerRef || 'N/A'}</p>
                  {selectedOrder?.approvalStatus && (
                    <p className="mb-0 small">
                      <strong>Status:</strong>{' '}
                      {selectedOrder.approvalStatus === 'pending' && (
                        <span className="badge bg-warning text-dark">Pending Approval</span>
                      )}
                      {selectedOrder.approvalStatus === 'approved' && (
                        <span className="badge bg-success">Approved</span>
                      )}
                      {selectedOrder.approvalStatus === 'rejected' && (
                        <span className="badge bg-danger">Rejected</span>
                      )}
                    </p>
                  )}
                </Card.Body>
              </Card>
            </Col>
            <Col md={6} className="d-flex">
              <Card className="shadow-sm flex-fill">
                <Card.Header className="py-1">
                  <h6 className="mb-0 d-inline-block me-3">Shipping Information</h6>
                </Card.Header>
                <Card.Body className="py-1 px-3">
                  <p className="mb-0 small"><strong>Ship To:</strong> {orderDetails.delName || 'N/A'}</p>
                  <p className="mb-0 small">{orderDetails.delAddress || ''}</p>
                  <p className="mb-0 small">
                    {[orderDetails.delCity, orderDetails.delState, orderDetails.delZip]
                      .filter(Boolean)
                      .join(', ')}
                  </p>
                  <p className="mb-0 small"><strong>Phone:</strong> {orderDetails.delPhone || 'N/A'}</p>
                  {orderDetails.delNotes && (
                    <p className="mb-0 small"><strong>Notes:</strong> {orderDetails.delNotes}</p>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Card className="shadow-sm mb-2">
            <Card.Header className="py-2">
              <h6 className="mb-0">Order Items</h6>
            </Card.Header>
            <Card.Body className="p-0" style={getCardStyle('items', orderDetails.items)}>
              <div className="table-responsive" style={{ margin: 0, padding: 0 }}>
                <Table size="sm" className="m-0">
                  <thead style={{ position: 'sticky', top: 0, background: 'white', zIndex: 1 }}>
                    <tr>
                      <th>Lot ID</th>
                      <th>Item Number</th>
                      <th>Description</th>
                      <th>Notes</th>
                      <th>PPU</th>
                      <th>Quantity</th>
                      <th>Total Pieces</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orderDetails.items && orderDetails.items.length > 0 ? (
                      orderDetails.items.map((item, index) => (
                        <tr key={item.idTrans2 || index}>
                          <td>{item.recTrans || 'N/A'}</td>
                          <td>{item.itemNumber || 'N/A'}</td>
                          <td>{item.itemName || 'N/A'}</td>
                          <td>{item.notes || '-'}</td>
                          <td>{Number(item.ppu || 1).toLocaleString()}</td>
                          <td>{Number(item.transQty || 0).toLocaleString()}</td>
                          <td>{Number((item.transQty || 0) * (item.ppu || 1)).toLocaleString()}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="7" className="text-center py-2">No items found</td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>

          <Row className="g-2 equal-height-row">
            <Col md={6} className="d-flex">
              <Card className="shadow-sm flex-fill">
                <Card.Header className="py-2">
                  <div className="d-flex justify-content-between align-items-center">
                    <h6 className="mb-0">Rates</h6>
                    <div className="text-end">
                      <span className="fw-bold">Grand Total: </span>
                      <span className="text-success fw-bold">{formatCurrency(grandTotal)}</span>
                    </div>
                  </div>
                </Card.Header>
                <Card.Body className="p-0 d-flex flex-column" style={getCardStyle('rates', orderDetails.rates)}>
                  <div className="table-responsive flex-fill" style={{ margin: 0, padding: 0 }}>
                    <Table size="sm" className="m-0">
                      <thead style={{ position: 'sticky', top: 0, background: 'white', zIndex: 1 }}>
                        <tr>
                          <th>Description</th>
                          <th>Qty</th>
                          <th>Rate</th>
                          <th>Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {orderDetails.rates && orderDetails.rates.length > 0 ? (
                          orderDetails.rates.map((rate, index) => (
                            <tr key={rate.idInvRating || index}>
                              <td>{rate.descr || 'N/A'}</td>
                              <td>{rate.qty || 0}</td>
                              <td>{formatCurrency(rate.rate)}</td>
                              <td>{formatCurrency((rate.qty || 0) * (rate.rate || 0))}</td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="4" className="text-center py-2">No rates found</td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            
            <Col md={6} className="d-flex">
              <Card className="shadow-sm flex-fill">
                <Card.Header className="py-2">
                  <h6 className="mb-0">Documents</h6>
                </Card.Header>
                <Card.Body className="p-0 d-flex flex-column" style={getCardStyle('documents', orderDetails.documents)}>
                  <div className="table-responsive flex-fill" style={{ margin: 0, padding: 0 }}>
                    <Table size="sm" className="m-0">
                      <thead style={{ position: 'sticky', top: 0, background: 'white', zIndex: 1 }}>
                        <tr>
                          <th>Description</th>
                        </tr>
                      </thead>
                      <tbody>
                        {/* Add Receipt document row at the top */}
                        <tr 
                          onClick={(e) => handleDocumentClick({ isReceipt: true }, e)}
                          style={{ cursor: 'pointer' }}
                          className="table-row-hover"
                        >
                          <td>
                            <FontAwesomeIcon icon={faFileAlt} className="me-2" />
                            Receipt for Order {selectedOrder?.idTrans}
                          </td>
                        </tr>
                        {/* Regular document rows */}
                        {orderDetails.documents && orderDetails.documents.length > 0 ? (
                          orderDetails.documents.map((doc, index) => (
                            <tr 
                              key={doc.idDocument || index}
                              onClick={(e) => handleDocumentClick(doc, e)}
                              style={{ cursor: 'pointer' }}
                              className="table-row-hover"
                            >
                              <td>
                                <FontAwesomeIcon icon={faFileAlt} className="me-2" />
                                {doc.descr || doc.fileName || 'Document'}
                              </td>
                            </tr>
                          ))
                        ) : (
                          orderDetails.documents.length === 0 && (
                            <tr>
                              <td className="text-center py-2">No additional documents found</td>
                            </tr>
                          )
                        )}
                      </tbody>
                    </Table>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer className="py-2">
          <Button variant="secondary" size="sm" onClick={() => setShowDetailModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Document Viewer Modal */}
      <DocumentViewerModal
        show={showDocumentModal}
        onHide={() => setShowDocumentModal(false)}
        document={selectedDocument}
      />

      <style>{`
        .modal-content {
          max-height: 90vh;
        }
        
        .modal-body {
          max-height: calc(90vh - 120px);
          overflow-y: auto;
          padding: 0.5rem !important;
        }

        .card {
          margin-bottom: 0 !important;
        }

        .card-header {
          background-color: #f8f9fa;
          border-bottom: 1px solid rgba(0,0,0,.125);
          padding: 0.5rem 0.75rem !important;
        }

        .card-header h6 {
          font-size: 0.875rem;
          margin: 0;
        }

        .card-body {
          background-color: white;
          padding: 0 !important;
        }

        /* Override card body padding for equal height cards */
        .equal-height-row .card-body {
          padding: 0 !important;
          min-height: auto !important;
          height: auto !important;
        }

        .small {
          font-size: 0.875rem;
          line-height: 1.4;
        }

        .table thead th {
          position: sticky;
          top: 0;
          background: white;
          z-index: 1;
          font-size: 0.875rem;
          padding: 0.25rem 0.5rem;
          border-bottom: 1px solid #dee2e6;
        }

        .table td {
          font-size: 0.875rem;
          padding: 0.25rem 0.5rem;
          vertical-align: middle;
          border-top: 1px solid #dee2e6;
        }

        .table {
          margin-bottom: 0 !important;
        }

        .table-sm thead th,
        .table-sm td {
          padding: 0.25rem 0.5rem;
        }

        /* Clickable table rows */
        .table tbody tr:hover {
          background-color: #f8f9fa !important;
        }

        /* Equal height cards using flexbox */
        .equal-height-row {
          display: flex;
          flex-wrap: wrap;
        }

        .equal-height-row .col-md-6 {
          display: flex !important;
        }

        .equal-height-row .card {
          display: flex;
          flex-direction: column;
          width: 100%;
        }

        .equal-height-row .card-body {
          flex: 1;
          display: flex;
          flex-direction: column;
        }

        .equal-height-row .table-responsive {
          flex: 1;
        }

        /* Clickable table rows */
        .table-row-hover:hover {
          background-color: #f8f9fa !important;
        }

        /* Origin and Destination card body padding */
        .modal .card-body.py-1.px-3 {
          padding-left: 1rem !important;
          padding-right: 1rem !important;
          padding-top: 0.25rem !important;
          padding-bottom: 0.25rem !important;
          min-height: auto !important;
          height: auto !important;
        }

        /* Force equal height for order info cards */
        .equal-height-row .card {
          min-height: 0 !important;
          height: auto !important;
        }

        .equal-height-row .card-body.py-1.px-3 {
          flex: 1 !important;
          display: flex !important;
          flex-direction: column !important;
          justify-content: flex-start !important;
          min-height: auto !important;
          height: auto !important;
        }

        /* Make cards more compact */
        .modal .card-body {
          min-height: auto !important;
          height: auto !important;
        }

        /* Compact table styling */
        .modal .table-sm {
          font-size: 0.875rem;
        }

        .modal .table-sm td,
        .modal .table-sm th {
          padding: 0.25rem 0.5rem !important;
          vertical-align: middle;
        }

        /* Remove extra spacing from table containers */
        .modal .table-responsive {
          margin: 0 !important;
          padding: 0 !important;
          min-height: auto !important;
          height: auto !important;
        }

        /* Force compact layout */
        .modal .table {
          margin-bottom: 0 !important;
          height: auto !important;
          min-height: auto !important;
        }

        /* Custom scrollbar styling */
        .modal-body::-webkit-scrollbar,
        div[style*="overflow-y: auto"]::-webkit-scrollbar {
          width: 6px;
        }

        .modal-body::-webkit-scrollbar-track,
        div[style*="overflow-y: auto"]::-webkit-scrollbar-track {
          background: #f1f1f1;
        }

        .modal-body::-webkit-scrollbar-thumb,
        div[style*="overflow-y: auto"]::-webkit-scrollbar-thumb {
          background: #888;
          border-radius: 3px;
        }

        .modal-body::-webkit-scrollbar-thumb:hover,
        div[style*="overflow-y: auto"]::-webkit-scrollbar-thumb:hover {
          background: #555;
        }
      `}</style>
    </Container>
  );
}

export default Orders;
